import { ScenesType } from 'common/apis/CmsAPI';
import { ToastNotification } from 'engage/components/Toast/Toast';
import VideoService from 'engage/service/VideoService';
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * This hook handles media updates for scenes in a storyboard.
 * It manages recording from Vormats extension, uploading, avatar generation, and scene insertion from the library.
 * Due to different extension versions, it handles various events and updates scene states accordingly.
 */

export const useSceneMediaUpdate = ({
  storyId,
  scenes,
  currentScene,
  addScene,
  updateScene,
  copyScenesIntoStory,
  deleteScene,
  checkSceneJobs,
  onExtensionInstall,
}) => {

  const { t } = useTranslation();

  /**
   * Recording/Uploading from the extension
   */
  const [extensionDraftSceneId, setExtensionDraftSceneId] = useState({ oldValue: null, newValue: null });

  const [recordingSceneId, setRecordingSceneId] = useState(null);

  const [isRecording, setIsRecording] = useState(false);

  const [copyClipToStory, setCopyClipToStory] = useState(null);

  // Due to extension always sends CANCEL_UPLOAD_A_DRAFT_SCENE message (was the upload canceled, succeeded, or the tab switched we need to check the scene on this event)
  const isCancelExtUploading = useRef(null);

  const cancelRecordingRef = React.useRef(null);

  const startRecording = useCallback(async (sceneId = null, index = 0, before = false) => {
    cancelRecordingRef.current = false;
    let sceneIdForExtension = sceneId;
    // No sceneId means we are creating a new scene
    if (!sceneIdForExtension) {
      const newSceneId = await addScene(index, before);
      updateScene(newSceneId, { hidden: true });
      sceneIdForExtension = newSceneId;
    }

    const scene = scenes.find(item => item.id === sceneId);

    if (!scene || (!scene.video || confirm(t('clip_organizer.confirm.rerecord')))) {
      const extensionId = process.env.EXT_ID;
      const extensionExists = document.getElementById(extensionId);

      if (extensionExists) {
        window.postMessage(
          {
            type: extensionId,
            message: 'OPEN_POPUP_IN_IFRAME',
            purpose: 'draft_recording',
            sceneInfo: { sceneId: sceneIdForExtension, storyId, script: scene?.script ?? '' },
          },
          '*'
        );
        setExtensionDraftSceneId({
          oldValue: null,
          newValue: sceneIdForExtension,
        });
      } else {
        window.location = `vormats-video-storyteller://?storyId=${storyId}&sceneId=${sceneIdForExtension}`;

        setTimeout(() => {
          if (localStorage.getItem('recorderInstalled') !== 'true') {
            onExtensionInstall();
          }
        }, 1000);
      }
    }
  }, [scenes, currentScene, storyId]);

  useEffect(() => {
    const storageListener = e => {
      switch (e.key) {
        case 'isClipRecording':
          setIsRecording(JSON.parse(e.newValue));
          break;
        case 'draftSceneId':
          setExtensionDraftSceneId({
            oldValue: e.oldValue,
            newValue: e.newValue,
          });
          break;
        default:
          break;
      }
    };

    window.addEventListener('storage', storageListener);

    return () => window.removeEventListener('storage', storageListener);
  }, []);

  const listenerFunc = async event => {
    const extensionId = process.env.EXT_ID;
    // We only accept messages from this window to itself [i.e. not from any iframes]
    if (event.source !== window) return;

    if (event.data.type && event.data.type === `${extensionId}-extension`) {
      switch (event.data.message) {
        case 'USER_MADE_A_CLIP':
          // This is how old extension handles new recorded scene. As we can't predict which extension user is using, 
          // we need to still support it. Basically, need to find a way to duplicate a scene from another video 
          if (event.data.storyId) {
            setCopyClipToStory(event.data.storyId);
          }
          break;
        case 'SAVE_UPDATED_SCRIPT':
          const { scriptSceneId, updatedStoryId, script } = event.data;
          if (updatedStoryId === storyId) {
            changeScene(scriptSceneId, { script });
          }
          break;
        case 'USER_CANCEL_RECORDING':
          let idToRemove = null;
          setRecordingSceneId(async sceneId => {
            idToRemove = sceneId;
            return null;
          });
          if (idToRemove) {
            cancelRecordingRef.current = true;
            setExtensionDraftSceneId({
              oldValue: idToRemove,
              newValue: null,
            });
          }
          break;
        case 'USER_CANCEL_A_DRAFT_SCENE':
          cancelRecordingRef.current = true;
          setExtensionDraftSceneId({
            oldValue: localStorage.getItem('draftSceneId'),
            newValue: null,
          });
          break;
        case 'UPDATE_DRAFT_SCENE_STATUS':
          const updatedSceneId = event.data.sceneId;
          setRecordingSceneId(updatedSceneId);

          if (updatedSceneId) {
            localStorage.setItem('draftSceneId', updatedSceneId);
          } else {
            localStorage.removeItem('draftSceneId');
          }
          break;
        case 'START_UPLOAD_A_DRAFT_SCENE':
          const uploadedSceneId = event.data.sceneId;
          setRecordingSceneId(uploadedSceneId);
          if (uploadedSceneId) {
            localStorage.setItem('draftSceneId', uploadedSceneId);

            setExtensionDraftSceneId({
              oldValue: null,
              newValue: uploadedSceneId,
            });
          }
          break;
        // This event is send when uploading is ether canceled or finished
        case 'CANCEL_UPLOAD_A_DRAFT_SCENE':
          isCancelExtUploading.current = true;
          const oldValue = localStorage.getItem('draftSceneId');
          localStorage.removeItem('draftSceneId');

          setExtensionDraftSceneId({
            oldValue,
            newValue: null
          });
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    if (copyClipToStory) {
      // To support old versions of extension when clip was recorded as part on new story
      copyScenesIntoStory([recordedClip.id]);
      setCopyClipToStory(null);
    }
  }, [copyClipToStory])

  useEffect(() => {
    window.addEventListener('message', listenerFunc);

    const extensionId = process.env.EXT_ID;
    window.postMessage({ type: extensionId, message: 'GET_DRAFT_SCENE_STATUS' }, '*');

    return () => window.removeEventListener('message', listenerFunc);
  }, []);

  useEffect(() => {
    // const storageListener = async ({ key, newValue, oldValue }) => {
    const draftSceneIdListener = async ({ oldValue, newValue }) => {
      if (newValue) {
        updateScene(newValue, { processing: false, recording: true }, true);
      } else {
        if (cancelRecordingRef.current) {
          const currentScene = scenes.find(item => item.id === oldValue);
          if (currentScene) {
            if (currentScene.hidden) {
              // If scene was hidden, remove it from the story
              deleteScene(oldValue, false);
            } else {
              updateScene(oldValue, { recording: false });
            }
          }
          return;
        }
        if (isCancelExtUploading.current && recordingSceneId !== null) {
          // Need to check a scene status after canceling/finishing upload
          // If not jobs found for scene, update scene to not recording and not processing
          updateScene(recordingSceneId, { reuploaded: true })
          checkSceneJobs(recordingSceneId, null, () => updateScene(recordingSceneId, { recording: false, processing: false, reuploaded: false }));
          setRecordingSceneId(null);
        }
        try {
          updateScene(oldValue, { processing: true, recording: false, hidden: false });
          checkSceneJobs(oldValue);
        } catch (e) {
          updateScene(oldValue, { processing: false, recording: false });
        }
      }

      setRecordingSceneId(newValue);
    };

    draftSceneIdListener(extensionDraftSceneId);
  }, [extensionDraftSceneId]);

  const cancelRecording = useCallback(async () => {
    cancelRecordingRef.current = true;

    const extensionId = process.env.EXT_ID;
    const extensionExists = document.getElementById(extensionId);
    try {
      if (extensionExists) {
        window.postMessage(
          {
            type: extensionId,
            message: 'CANCEL_FROM_WEB_APP',
          },
          '*'
        );
      }
      updateScene(recordingSceneId, { processing: false, recording: false }, true);
    } catch (error) {
      console.error('useSceneMediaUpdate Error while canceling recording', error);
    } finally {
      const removeLocalStorageItem = (key) => {
        localStorage.getItem(key) && localStorage.removeItem(key);
      }
      setTimeout(() => {
        // Clear the local storage
        ['recordedStoryId', 'updateClipScript', 'userCancelDraftScene', 'isClipRecording', 'draftSceneId'].forEach((key, index) =>
          removeLocalStorageItem(key));
      }, 1000);
    }
  }, [recordingSceneId]);

  const visibilityListener = () => {
    if (document.visibilityState == 'visible') {
      setTimeout(async () => {
        const recordedStoryId = window.localStorage.getItem('recordedStoryId');
        if (recordedStoryId) {
          try {
            const story = await VideoService.getVideoDetails(recordedStoryId, ScenesType.REGULAR);
            setRecordedClip({ ...story.scenes[0], author: story.author, storyId: story.id, source: 'recorded' });
          } catch (e) {
            console.log('Scene with this ID doesnt exist', recordedStoryId);
          }
          window.localStorage.removeItem('recordedStoryId');
        }

        const updateClipScriptData = window.localStorage.getItem('updateClipScript');
        if (updateClipScriptData) {
          const parsedData = JSON.parse(updateClipScriptData);
          if (parsedData.storyId === storyId) {
            changeScene(parsedData.sceneId, { script: parsedData.script });
          }
          window.localStorage.removeItem('updateClipScript');
        }

        const recordingIsCanceled = window.localStorage.getItem('userCancelDraftScene');
        if (recordingIsCanceled) {
          cancelRecordingRef.current = true;
          localStorage.removeItem('userCancelDraftScene');
        }
      }, 250);
    }
  };

  useEffect(() => {
    window.addEventListener('visibilitychange', visibilityListener);
    return () => {
      window.removeEventListener('visibilitychange', visibilityListener);
    };
  }, []);



  /**
   * Uploading from the page
   */
  const [uploadClipInfo, setUploadClipInfo] = useState({});
  const [showUploadingModal, setShowUploadingModal] = useState(false);


  const cancelUploading = useCallback(() => {
    setUploadClipInfo({});
    setShowUploadingModal(false);

    const canceledForScene = scenes.find(item => item.id === uploadClipInfo.sceneId);
    if (canceledForScene && canceledForScene.hidden) {
      deleteScene(canceledForScene.id, false);
    }
  }, [scenes, uploadClipInfo]);

  const startUploading = useCallback(async (sceneId = null, index = 0, before = false) => {
    if (!sceneId) {
      const newSceneId = await addScene(index, before);
      updateScene(newSceneId, { hidden: true });
      setUploadClipInfo({ sceneId: newSceneId, storyId, isDraft: true });
    } else {
      const scene = scenes.find(item => item.id === sceneId);
      setUploadClipInfo({ sceneId, storyId, isDraft: !sceneId || !scene?.video });
    }
    setShowUploadingModal(true);
  }, [storyId, scenes]);

  const uploadingDone = useCallback(() => {
    checkSceneJobs(uploadClipInfo.sceneId);
    setUploadClipInfo({});
    setShowUploadingModal(false);
  }, [uploadClipInfo])



  /**
   * Avatar generation
   */

  const [avatarSceneId, setAvatarSceneId] = useState(null);
  const startAvatar = useCallback((sceneId) => {
    const scene = scenes.find(item => item.id === sceneId);
    // Open modal if clip is not recorded ot user approved to re-record
    if (!scene?.video || confirm(t('clip_organizer.confirm.rerecord'))) {
      setAvatarSceneId(scene.id);
    }
  }, [storyId, scenes])

  const cancelAvatar = () => {
    setAvatarSceneId(null);
  };

  const selectAvatar = useCallback(async ([{ avatarId, voiceId }]) => {
    try {
      const { id: jobId } = await VideoService.generateAvatarClip(storyId, avatarSceneId, avatarId, voiceId);
      // Updated already recorded scene by showing scene processing & removing scene resources
      updateScene(avatarSceneId, { processing: true }, true);
      checkSceneJobs(avatarSceneId, jobId);
      setAvatarSceneId(null);
    } catch (e) {
      console.error('Error generating avatar clip', e);
      ToastNotification({
        message: t('clip_organizer.generate_avatar.failed'),
      });
    }
  }, [avatarSceneId]);


  /**
   * Insert scenes from library
   */
  const [insertSelectionInfo, setInsertSelectionInfo] = useState(null);

  const startSelectFromLibrary = useCallback((index = 0, before = false) => {
    setInsertSelectionInfo({ index, before });
  }, []);

  const selectionDone = useCallback((selectedClips) => {
    setInsertSelectionInfo(null);
    copyScenesIntoStory(selectedClips.map(item => item.id), insertSelectionInfo.index, insertSelectionInfo.before);
  }, [copyScenesIntoStory, insertSelectionInfo]);

  return {
    isRecording,
    startRecording,
    cancelRecording,
    showUploadingModal,
    uploadClipInfo,
    cancelUploading,
    startUploading,
    uploadingDone,
    showAvatarModal: avatarSceneId !== null,
    avatarSceneId,
    startAvatar,
    cancelAvatar,
    selectAvatar,
    showLibraryModal: insertSelectionInfo !== null,
    startSelectFromLibrary,
    cancelSelectFromLibrary: () => setInsertSelectionInfo(null),
    selectionDone
  };
}